
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type NotificationSettings } from '@shared/user';
import { Badge, Card, Heading, Row, Stack, Text } from '@client/shared/design';
import {
  updateMyNotificationSettings,
  VIEWER_QUERY_KEY,
  type Viewer,
} from './viewer-operations.ts';
import { Toggle } from './Toggle.tsx';
import styles from './MyAccount.module.css';

const DISCRETIONARY: ReadonlyArray<{
  key: keyof NotificationSettings;
  title: string;
  description: string;
}> = [
  {
    key: 'listingAdded',
    title: 'New listing available to you',
    description: 'When a listing you’re allowed to book is added to your group.',
  },
  {
    key: 'bookingCreated',
    title: 'Open house booked on a listing you follow',
    description: "When someone books an open house on a listing you’re set up to receive notifications for.",
  },
  {
    key: 'listingUpdated',
    title: 'Listing updated',
    description: 'When the details of a listing you follow change.',
  },
];

export interface NotificationsBlockProps {
  settings: NotificationSettings;
}

export function NotificationsBlock({ settings }: NotificationsBlockProps) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: updateMyNotificationSettings,
    onMutate: async (next: NotificationSettings) => {
      await queryClient.cancelQueries({ queryKey: VIEWER_QUERY_KEY });
      const previous = queryClient.getQueryData<Viewer>(VIEWER_QUERY_KEY);
      if (previous) {
        queryClient.setQueryData<Viewer>(VIEWER_QUERY_KEY, {
          ...previous,
          notificationSettings: next,
        });
      }
      return { previous };
    },
    onError: (_err, _next, context) => {
      if (context?.previous) queryClient.setQueryData(VIEWER_QUERY_KEY, context.previous);
    },
    onSuccess: (viewer: Viewer) => {
      queryClient.setQueryData(VIEWER_QUERY_KEY, viewer);
    },
  });

  function handleToggle(key: keyof NotificationSettings, checked: boolean) {
    setError(null);
    const current = queryClient.getQueryData<Viewer>(VIEWER_QUERY_KEY)?.notificationSettings ?? settings;
    const next: NotificationSettings = { ...current, [key]: checked };
    mutation.mutate(next, {
      onError: () => setError("Couldn’t update that setting. Please try again."),
    });
  }

  return (
    <Card pad="lg" spine as="section" aria-labelledby="notifications-heading">
      <Stack gap={4}>
        <Stack gap={1}>
          <Heading level={3} as="h2" id="notifications-heading">
            Email notifications
          </Heading>
          <Text size="sm" tone="soft">
            Choose which emails you’d like to receive. Turning one off stops that email from the
            next send on.
          </Text>
        </Stack>

        <div>
          {DISCRETIONARY.map(({ key, title, description }) => (
            <div className={styles.toggleRow} key={key}>
              <Stack gap={0} className={styles.toggleCopy}>
                <Row gap={2} wrap>
                  <Text weight="medium">{title}</Text>
                </Row>
                <Text size="sm" tone="muted">
                  {description}
                </Text>
              </Stack>
              <Toggle
                checked={settings[key]}
                aria-label={title}
                onChange={(checked) => handleToggle(key, checked)}
              />
            </div>
          ))}

          <div className={styles.toggleRow}>
            <Stack gap={0} className={styles.toggleCopy}>
              <Row gap={2} wrap>
                <Text weight="medium">Booking confirmation</Text>
                <Badge tone="ok" dot>
                  Always on
                </Badge>
              </Row>
              <Text size="sm" tone="muted">
                {"Confirms every open house you book. This one can’t be turned off so you never miss an open house you’re scheduled for."}
              </Text>
            </Stack>
            <Toggle checked disabled aria-label="Booking confirmation (always on)" />
          </div>
        </div>

        {error && (
          <Text size="sm" tone="danger" role="alert">
            {error}
          </Text>
        )}
      </Stack>
    </Card>
  );
}
